
import { supabase } from './supabase';

export interface PriceLock {
  id: string;
  dealer_id: string;
  user_id: string | null;
  metal: 'ORO' | 'ARGENTO';
  purity: string;
  price: number;
  weight: number;
  status: 'PENDING' | 'ACTIVE' | 'EXPIRED' | 'CANCELLED';
  expires_at: string;
  created_at: string;
  contact_info: {
    full_name: string;
    email: string;
    phone: string;
    address?: string;
  };
}

/**
 * Fetches active price locks for an authenticated user
 */
export const fetchUserPriceLocks = async (userId: string): Promise<PriceLock[]> => {
  try {
    const { data, error } = await supabase
      .from('checkpoints')
      .select('*')
      .eq('user_id', userId)
      .order('created_at', { ascending: false });
    
    if (error) {
      throw error;
    }
    
    return data || [];
  } catch (error) {
    console.error('Error fetching user price locks:', error);
    return [];
  }
};

/**
 * Fetches price locks for a specific dealer
 */
export const fetchDealerPriceLocks = async (dealerId: string, userId: string): Promise<PriceLock[]> => {
  try {
    const { data, error } = await supabase
      .from('checkpoints')
      .select('*')
      .eq('dealer_id', dealerId)
      .eq('user_id', userId)
      .order('created_at', { ascending: false });
    
    if (error) {
      throw error;
    }
    
    return data || [];
  } catch (error) {
    console.error('Error fetching dealer price locks:', error);
    return [];
  }
};

/**
 * Cancels a price lock
 */
export const cancelPriceLock = async (lockId: string): Promise<boolean> => {
  try {
    const { error } = await supabase
      .from('checkpoints')
      .update({ status: 'CANCELLED' })
      .eq('id', lockId);
    
    if (error) {
      throw error;
    }
    
    return true;
  } catch (error) {
    console.error('Error cancelling price lock:', error);
    return false;
  }
};

/**
 * Gets local price locks for non-authenticated users
 */
export const getLocalPriceLocks = (): PriceLock[] => {
  try {
    const savedRequests = localStorage.getItem('priceLockRequests');
    if (!savedRequests) return [];
    
    return JSON.parse(savedRequests);
  } catch (error) {
    console.error('Error reading local price locks:', error);
    return [];
  }
};

/**
 * Syncs local price locks with user account after login
 */
export const syncLocalPriceLocks = async (userId: string): Promise<boolean> => {
  try {
    const localLocks = getLocalPriceLocks();
    if (!localLocks.length) return true;
    
    // Update each local lock with the user ID
    const updatedLocks = localLocks.map(lock => ({
      ...lock,
      user_id: userId
    }));
    
    // Insert the updated locks into Supabase
    const { error } = await supabase
      .from('checkpoints')
      .insert(updatedLocks);
    
    if (error) {
      throw error;
    }
    
    // Clear local storage
    localStorage.removeItem('priceLockRequests');
    
    return true;
  } catch (error) {
    console.error('Error syncing local price locks:', error);
    return false;
  }
};

/**
 * Checks if any price locks have expired and updates their status
 */
export const checkExpiredPriceLocks = async (): Promise<void> => {
  try {
    const now = new Date().toISOString();
    
    // Update expired locks
    await supabase
      .from('checkpoints')
      .update({ status: 'EXPIRED' })
      .eq('status', 'ACTIVE')
      .lt('expires_at', now);
    
  } catch (error) {
    console.error('Error checking expired price locks:', error);
  }
};
